import clsx from 'clsx';
import PropTypes from 'prop-types';
import { filter } from 'ramda';
import React from 'react';
import Select from 'react-select';

import CancelIcon from '@material-ui/icons/Cancel';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import red from '@material-ui/core/es/colors/red';

import { emphasize, makeStyles } from '@material-ui/core/styles';


const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    height: 250,
    minWidth: 290,
  },
  formControl: {
    width: '100%',
  },
  formControlError: {
    color: 'red',
  },
  input: {
    display: 'flex',
    padding: 0,
    height: 'auto',
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: theme.spacing(0.5, 0.25),
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  chipError: {
    backgroundColor: emphasize(theme.palette.type === 'light' ? red[300] : red[700],0.08),
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    bottom: 6,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(2),
  },
}));

const NoOptionsMessage = ({
  children, innerProps, selectProps
}) => (
  <Typography
    color="textSecondary"
    className={selectProps.classes.noOptionsMessage}
    {...innerProps}
  >
    {children && children}
  </Typography>
);

NoOptionsMessage.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]),
};

const Control = ({
  children,
  innerProps,
  innerRef,
  selectProps: { classes, TextFieldProps },
}) => (
  <FormControl className={classes.formControl}>
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />

    {TextFieldProps.error && TextFieldProps.error[0] && (
      <FormHelperText className={classes.formControlError}>{TextFieldProps.error[0]}</FormHelperText>
    )}
  </FormControl>
);

Control.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({
    onMouseDown: PropTypes.func.isRequired,
  }).isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]).isRequired,
  selectProps: PropTypes.object.isRequired,
};

const Option = ({
  children, innerProps, innerRef, isFocused, isSelected,
}) => (
  <MenuItem
    ref={innerRef}
    selected={isFocused}
    component="div"
    style={{
      fontWeight: isSelected ? 500 : 400,
    }}
    {...innerProps}
  >
    {children && children}
  </MenuItem>
);

Option.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    onMouseMove: PropTypes.func.isRequired,
    onMouseOver: PropTypes.func.isRequired,
    tabIndex: PropTypes.number.isRequired,
  }).isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]).isRequired,
  isFocused: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

const Placeholder = ({
  children, innerProps = {}, selectProps,
}) => (
  <Typography color="textSecondary" className={selectProps.classes.placeholder} {...innerProps}>
    {children && children}
  </Typography>
);

Placeholder.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

const SingleValue = ({
  children, innerProps, selectProps,
}) => (
  <Typography className={selectProps.classes.singleValue} {...innerProps}>
    {children && children}
  </Typography>
);

SingleValue.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.any.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const ValueContainer = ({
  children, selectProps,
}) => <div className={selectProps.classes.valueContainer}>{children}</div>;

ValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
};

const MultiValue = ({
  children, data, isFocused, removeProps, selectProps,
}) => (
  <Chip
    tabIndex={-1}
    label={children}
    className={clsx(selectProps.classes.chip, {
      [selectProps.classes.chipFocused]: isFocused,
      [selectProps.classes.chipError]:
        selectProps.error && selectProps.error.length > 0 && data.value === selectProps.error[1][0],
    })}
    onDelete={removeProps.onClick}
    deleteIcon={<CancelIcon {...removeProps} />}
  />
);

MultiValue.propTypes = {
  children: PropTypes.node,
  data: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  isFocused: PropTypes.bool.isRequired,
  removeProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    onMouseDown: PropTypes.func.isRequired,
    onTouchEnd: PropTypes.func.isRequired,
  }).isRequired,
  selectProps: PropTypes.object.isRequired,
};

const Menu = ({
  children, innerProps, selectProps,
}) => (
  <Paper square className={selectProps.classes.paper} {...innerProps}>
    {children}
  </Paper>
);

Menu.propTypes = {
  children: PropTypes.element.isRequired,
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const AutoCompleteWithChipsField = ({
    id, label,
    input: { value, onChange },
    meta: { touched, error },
    shadeField, suggestions,
    initialValues,
}) => {
  const classes = useStyles();

  const selectedValue = value && value.length > 0 && value[0].constructor === Object
    ? value : filter(s => value.includes(s.value))(suggestions);

  return (
    <div style={{
      background: shadeField && 'repeating-linear-gradient(135deg, rgba(0,0,0,0.07), rgba(0,0,0,0.07) .05em, #f5f5f5 0, #f5f5f5 .75em)',
    }}>
      <Select
        isMulti
        error={error}
        touched={touched}
        classes={classes}
        inputId={id}
        TextFieldProps={{
          error,
          touched,
          label,
          InputLabelProps: {
            htmlFor: id,
            shrink: true,
          },
        }}
        placeholder={label}
        options={suggestions}
        components={{
          Control,
          Menu,
          MultiValue,
          NoOptionsMessage,
          Option,
          Placeholder,
          SingleValue,
          ValueContainer,
        }}
        value={initialValues || selectedValue}
        onChange={values => onChange((values || []).map(v => v.value))}
      />
    </div>
  );
};

export default AutoCompleteWithChipsField;
